import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';

import { VariableLinkText } from 'src/app/utils/tip-tap-extensions/variable';
import { CUSTOM_FIELD_TYPES } from 'src/models'; // Assuming these are the correct model paths
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

const CUSTOM_FIELD_SIGIL = '__CUSTOM_FIELD__';

const CATEGORIES = {
  CONVERSATION: {
    label: 'Conversation Field',
    id: 'conversation',
  },
  AUTOMATION_INPUT: {
    label: 'Automation Input Field',
    id: 'automationInput',
  },
  TRIGGERING_USER: {
    label: 'Triggering User Field',
    id: 'triggeringUser',
  },
  CHANNEL: {
    label: 'Channel Field',
    id: 'channel',
  },
};

interface VariableField {
  path: string;
  label: string;
  type?: 'link' | 'user-mention' | 'slack-channel-mention' | 'slack-usergroup-mention';
}

@Component({
  selector: 'app-variable-config-modal',
  templateUrl: './variable-config-modal.component.html',
  styleUrls: ['./variable-config-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
})
export class VariableConfigModalComponent {
  selectedVariable?: {
    path: string;
    label: string;
    type?: 'link' | 'user-mention' | 'slack-channel-mention' | 'slack-usergroup-mention';
    text?: VariableLinkText;
  };

  selectedCategory?: {
    id: string;
    title: string;
  };

  CATEGORIES = CATEGORIES;

  selectedConversationField?: {
    id: string;
    title: string;
  };

  selectedCustomField?: {
    id: string;
    title: string;
  };

  conversationFields: VariableField[] = [
    { path: 'conversation.title', label: 'Conversation Title' },
    { path: 'conversation.id', label: 'Conversation ID' },
    { path: 'conversation.friendlyId', label: 'Conversation Friendly ID' },
    { path: 'conversation.status', label: 'Conversation Status Type' },
    { path: 'conversation.status.name', label: 'Conversation Status Name' },
    { path: 'conversation.summary', label: 'Conversation Summary' },
    { path: 'conversation.priorityLabel', label: 'Conversation Priority' },
    { path: 'conversation.priorityIcon', label: 'Conversation Priority Icon' },
    { path: 'conversation.webUrl', label: 'Conversation Unthread URL 🔗', type: 'link' },
    { path: 'conversation.slackUrl', label: 'Conversation Slack URL 🔗', type: 'link' },
    { path: 'conversation.slackChannel', label: 'Conversation Channel 💬', type: 'slack-channel-mention' },
    { path: 'conversation.submitterUser', label: 'Conversation Submitter 👤', type: 'user-mention' },
    { path: 'conversation.submitterUserId', label: 'Conversation Submitter User ID' },
    { path: 'conversation.submitterUser.name', label: 'Conversation Submitter User Name' },
    { path: 'conversation.submitterUser.email', label: 'Conversation Submitter User Email' },
    { path: 'conversation.submitterUser.slackId', label: 'Conversation Submitter User Slack ID' },
    { path: 'conversation.assignedToUser', label: 'Conversation Assignee 👤', type: 'user-mention' },
    { path: 'conversation.assignedToUserId', label: 'Conversation Assignee User ID' },
    { path: 'conversation.assignedToUser.name', label: 'Conversation Assignee User Name' },
    { path: 'conversation.assignedToUser.email', label: 'Conversation Assignee User Email' },
    { path: 'conversation.assignedToUser.slackId', label: 'Conversation Assignee User Slack ID' },
    { path: CUSTOM_FIELD_SIGIL, label: 'Custom Field' },
  ];

  selectedTriggeringUserField?: {
    id: string;
    title: string;
  };

  triggeringUserFields: VariableField[] = [
    { path: 'user', label: 'Triggering User 👤', type: 'user-mention' },
    { path: 'user.name', label: 'Triggering User Name' },
    { path: 'user.email', label: 'Triggering User Email' },
    { path: 'user.slackId', label: 'Triggering User Slack ID' },
  ];

  selectedChannelField?: {
    id: string;
    title: string;
  };

  channelFields: VariableField[] = [
    { path: 'customer.name', label: 'Channel Name' },
    { path: 'customer.id', label: 'Channel ID' },
  ];

  selectedAutomation?: {
    id: string;
    title: string;
    inputFields: any[];
  };

  selectedAutomationInputField?: {
    id: string;
    title: string;
  };

  linkTextIsVariable = false;
  staticLinkText = '';
  variableLinkText?: {
    path: string;
    label: string;
  };

  CUSTOM_FIELD_SIGIL = CUSTOM_FIELD_SIGIL;

  // Add a property to track if we're in link mode
  isLinkMode = false;

  constructor(
    private modalCtrl: ModalController,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  showCategoryOptions() {
    this.searchablePopoverService.showPopover({
      event: event,
      showImages: false,
      showSearch: true,
      selectedItemId: this.selectedCategory?.id,
      callback: (id: string, title: string) => {
        if (!id || id === this.selectedCategory?.id) {
          this.selectedCategory = null;
        } else {
          this.selectedCategory = { id, title };
        }
      },
      loadData: async (searchText: string) => {
        return Object.values(CATEGORIES)
          .map((c) => ({ id: c.id, title: c.label, object: c }))
          .filter((c) => !searchText || c.title.toLowerCase().includes(searchText.toLowerCase()));
      },
    });
  }

  showConversationFieldOptions() {
    this.searchablePopoverService.showPopover({
      event: event,
      showImages: false,
      showSearch: true,
      selectedItemId: this.selectedConversationField?.id,
      callback: (id: string, title: string, object: any) => {
        if (!id || id === this.selectedConversationField?.id) {
          this.selectedConversationField = null;
        } else {
          this.selectedConversationField = { id, title };
        }

        if (!this.selectedConversationField || this.selectedConversationField.id === CUSTOM_FIELD_SIGIL) {
          this.selectedVariable = null;
        } else {
          this.selectedVariable = object;
        }
      },
      loadData: async (searchText: string) => {
        // Use the filtered fields based on link mode
        return this.filteredConversationFields
          .map((c) => ({ id: c.path, title: c.label, object: c }))
          .filter((c) => !searchText || c.title.toLowerCase().includes(searchText.toLowerCase()));
      },
    });
  }

  showCustomFieldOptions() {
    this.searchablePopoverService.customFields({
      event: event,
      allowedTypes: [
        CUSTOM_FIELD_TYPES.SHORT_ANSWER,
        CUSTOM_FIELD_TYPES.LONG_ANSWER,
        CUSTOM_FIELD_TYPES.SINGLE_SELECT,
        CUSTOM_FIELD_TYPES.USER_SELECT,
        CUSTOM_FIELD_TYPES.USER_GROUP_SELECT,
      ],
      showIcon: true,
      selectedValue: this.selectedCustomField?.id,
      callback: (id: string, title: string, object: any) => {
        if (!id || id === this.selectedCustomField?.id) {
          this.selectedCustomField = null;
        } else {
          this.selectedCustomField = { id, title };
        }

        if (!this.selectedConversationField) {
          this.selectedVariable = null;
        } else {
          this.selectedVariable = {
            path: `conversation.ticketTypeFieldsExpanded['${this.selectedCustomField.id}']`,
            label: `${this.selectedCustomField.title}`,
            type: this.customFieldTypeToVariableType(object.type),
          };
        }
      },
    });
  }

  showChannelFieldOptions() {
    this.searchablePopoverService.showPopover({
      event: event,
      showImages: false,
      showSearch: true,
      selectedItemId: this.selectedChannelField?.id,
      callback: (id: string, title: string, object: any) => {
        if (!id || id === this.selectedChannelField?.id) {
          this.selectedChannelField = null;
        } else {
          this.selectedChannelField = { id, title };
        }

        if (!this.selectedChannelField) {
          this.selectedVariable = null;
        } else {
          this.selectedVariable = object;
        }
      },
      loadData: async (searchText: string) => {
        // Use the filtered fields based on link mode
        const fields = this.isLinkMode
          ? this.channelFields.filter((field) => field.type === 'link')
          : this.channelFields;

        return fields
          .map((c) => ({ id: c.path, title: c.label, object: c }))
          .filter((c) => !searchText || c.title.toLowerCase().includes(searchText.toLowerCase()));
      },
    });
  }

  showTriggeringUserFieldOptions() {
    this.searchablePopoverService.showPopover({
      event: event,
      showImages: false,
      showSearch: true,
      selectedItemId: this.selectedTriggeringUserField?.id,
      callback: (id: string, title: string, object: any) => {
        if (!id || id === this.selectedTriggeringUserField?.id) {
          this.selectedTriggeringUserField = null;
        } else {
          this.selectedTriggeringUserField = { id, title };
        }

        if (!this.selectedTriggeringUserField) {
          this.selectedVariable = null;
        } else {
          this.selectedVariable = object;
        }
      },
      loadData: async (searchText: string) => {
        // Use the filtered fields based on link mode
        const fields = this.isLinkMode
          ? this.triggeringUserFields.filter((field) => field.type === 'link')
          : this.triggeringUserFields.filter((field) => field.type !== 'link');

        return fields
          .map((c) => ({ id: c.path, title: c.label, object: c }))
          .filter((c) => !searchText || c.title.toLowerCase().includes(searchText.toLowerCase()));
      },
    });
  }

  showAutomationOptions() {
    this.searchablePopoverService.automations({
      event: event,
      trigger: {
        type: ['manual'],
      },
      selectedAutomationId: this.selectedAutomation?.id,
      callback: (id: string, title: string, object: any) => {
        if (!id || id === this.selectedAutomation?.id) {
          this.selectedAutomation = null;
        } else {
          this.selectedAutomation = { id, title, inputFields: object.trigger?.data?.inputFields ?? [] };
        }
      },
    });
  }

  showAutomationInputFieldOptions() {
    this.searchablePopoverService.showPopover({
      event: event,
      showImages: false,
      showSearch: true,
      selectedItemId: this.selectedAutomationInputField?.id,
      callback: (id: string, title: string, object: any) => {
        if (!id || id === this.selectedAutomationInputField?.id) {
          this.selectedAutomationInputField = null;
        } else {
          this.selectedAutomationInputField = { id, title };
        }

        if (!this.selectedAutomationInputField) {
          this.selectedVariable = null;
        } else {
          this.selectedVariable = {
            path: `customMapExpanded['${this.selectedAutomationInputField.id}']`,
            label: `[${this.selectedAutomation.title}] ${this.selectedAutomationInputField.title}`,
            type: this.customFieldTypeToVariableType(object.type),
          };
        }
      },
      loadData: async (searchText: string) => {
        return this.selectedAutomation?.inputFields
          .map((c) => {
            const type = this.customFieldTypeToVariableType(c.type);

            return {
              id: c.id,
              title: `${c.label}${type === 'slack-usergroup-mention' ? ' 👥' : type === 'user-mention' ? ' 👤' : ''}`,
              object: c,
            };
          })
          .filter((c) => !searchText || c.title.toLowerCase().includes(searchText.toLowerCase()));
      },
    });
  }

  showOptionsForLinkText() {
    this.searchablePopoverService.showPopover({
      event: event,
      showImages: false,
      showSearch: true,
      selectedItemId: this.selectedConversationField?.id,
      callback: (id: string, title: string, object: any) => {
        if (!id || id === this.variableLinkText?.path) {
          this.variableLinkText = null;
        } else {
          this.variableLinkText = { path: id, label: title };
        }
      },
      loadData: async (searchText: string) => {
        return this.conversationFields
          .filter((c) => !c.type && c.path !== CUSTOM_FIELD_SIGIL)
          .map((c) => ({ id: c.path, title: c.label, object: c }))
          .filter((c) => !searchText || c.title.toLowerCase().includes(searchText.toLowerCase()));
      },
    });
  }

  insert() {
    if (!this.selectedVariable?.path) {
      return;
    }

    this.modalCtrl.dismiss({
      ...this.selectedVariable,
      text: {
        isVariable: this.linkTextIsVariable,
        path: this.variableLinkText?.path,
        text: this.staticLinkText,
        label: this.variableLinkText?.label,
      },
    });
  }

  dismiss() {
    this.modalCtrl.dismiss(null);
  }

  isValid() {
    // Update validation to check if we have a valid selection based on mode
    if (!this.selectedVariable) {
      return false;
    }

    if (this.isLinkMode && (!this.selectedVariable || this.selectedVariable.type !== 'link')) {
      return false;
    }

    return true;
  }

  // Add click handlers for the text/link toggle
  toggleTextMode() {
    this.linkTextIsVariable = false;
    this.isLinkMode = false;

    // Reset selected variable if it was a link type
    if (this.selectedVariable?.type === 'link') {
      this.selectedVariable = undefined;
      this.resetSelections();
    }
  }

  toggleLinkMode() {
    this.isLinkMode = true;

    // Reset selected variable if it wasn't a link type
    if (this.selectedVariable && this.selectedVariable.type !== 'link') {
      this.selectedVariable = undefined;
      this.resetSelections();
    }
  }

  // Helper method to reset selections when changing modes
  resetSelections() {
    this.selectedCategory = undefined;
    this.selectedConversationField = undefined;
    this.selectedCustomField = undefined;
    this.selectedChannelField = undefined;
    this.selectedTriggeringUserField = undefined;
    this.selectedAutomation = undefined;
    this.selectedAutomationInputField = undefined;
  }

  // Filter conversation fields based on link mode
  get filteredConversationFields() {
    return this.conversationFields.filter((field) => (this.isLinkMode ? field.type === 'link' : true));
  }

  customFieldTypeToVariableType(customFieldType: string) {
    const type =
      customFieldType === CUSTOM_FIELD_TYPES.USER_SELECT
        ? 'user-mention'
        : customFieldType === CUSTOM_FIELD_TYPES.USER_GROUP_SELECT
          ? 'slack-usergroup-mention'
          : undefined;

    return type;
  }
}
